import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, ShieldCheck, BadgeCheck, Leaf, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Photo } from "@/components/Photo";
import { QuoteBand } from "@/components/QuoteBand";
import { IMAGES } from "@/data/content";

const trustItems = [
  { icon: HomeIcon, label: "Family-Run" },
  { icon: ShieldCheck, label: "Fully Insured" },
  { icon: BadgeCheck, label: "Enhanced DBS Checked" },
  { icon: Leaf, label: "COSHH Certified" },
  { icon: Users, label: "Trusted & Reliable" },
];

const Home = () => {
  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      {/* HERO — asymmetric editorial cover */}
      <section className="bg-marble border-b border-slate-300/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-16 sm:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            {/* Hero renders instantly (no reveal) — it mirrors the static shell in index.html for a fast first paint */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4">
                <span className="hidden sm:block w-10 h-px bg-rosegold-metallic" />
                <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] sm:tracking-[0.3em] text-emerald-700">
                  Family-run · Professional · Personal
                </p>
              </div>
              <h1 className="font-accent text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.04] text-slate-900 mt-7">
                A cleaner home
                <br />
                for{" "}
                <span className="italic text-rosegold-deep border-b-[3px] border-rosegold/50">
                  brighter days.
                </span>
              </h1>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed mt-7 max-w-lg first-letter:font-accent first-letter:text-5xl first-letter:text-rosegold-deep first-letter:float-left first-letter:mr-2 first-letter:leading-[0.85] first-letter:mt-1">
                Professional cleaning, tailored to your home and your life. Five Point Cleaning is a
                small, family-run team delivering high-quality, reliable cleans with absolutely no
                judgement.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4 sm:pl-10">
                <Link
                  to="/contact"
                  data-testid="hero-request-quote-btn"
                  className="btn btn-primary"
                >
                  Get Your Free Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-our-services-btn"
                  className="btn btn-secondary"
                >
                  Our Services
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative">
                <Photo fetchPriority="high" decoding="async"
                  src={IMAGES.hero}
                  sizes="(max-width: 1024px) 100vw, 40vw"
                  width="1400"
                  height="933"
                  alt="A bright, freshly cleaned kitchen with large windows looking onto the garden"
                  className="rounded-2xl object-cover w-full h-[320px] sm:h-[420px] lg:h-[480px] shadow-2xl shadow-emerald-900/20"
                  data-testid="hero-image"
                />
                <Photo
                  src={IMAGES.heroDetail}
                  sizes="192px"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="hidden sm:block absolute -bottom-10 -left-10 w-40 h-40 lg:w-48 lg:h-48 object-cover rounded-xl border-4 border-slate-50 shadow-xl shadow-emerald-900/20 rotate-[-4deg]"
                />
                <div className="absolute -top-5 right-1 sm:-right-5 bg-rosegold-metallic text-blush rounded-full w-28 h-28 sm:w-32 sm:h-32 flex flex-col items-center justify-center text-center shadow-xl shadow-rosegold-base/40 rotate-6">
                  <ShieldCheck className="w-6 h-6 mb-1" />
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider leading-tight px-3">
                    Fully Insured · DBS Checked
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST RIBBON — architectural numbered strip */}
      <section className="bg-emerald-100 border-b border-emerald-600/10" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            {trustItems.map(({ icon: Icon, label }, i) => (
              <Reveal
                key={label}
                delay={0.05 * i}
                className={`flex flex-col items-start gap-2 py-8 px-5 ${
                  i > 0 ? "border-l border-emerald-600/15" : ""
                } ${i >= 2 ? "max-lg:border-t max-lg:border-emerald-600/15" : ""} ${
                  i === 2 || i === 4 ? "max-sm:border-l-0" : ""
                } ${i === 4 ? "max-sm:col-span-2 max-sm:flex-row max-sm:items-center max-sm:gap-4 max-sm:py-6" : ""}`}
              >
                <span className="font-accent text-3xl font-light text-slate-900/20 select-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="w-12 h-12 rounded-full bg-blush border border-slate-300/50 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
                </span>
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-slate-700 leading-snug">
                  {label}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <QuoteBand testId="home-quote-band" />
    </div>
  );
};

export default Home;
